import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import FAQSection from '@/components/ui/FAQSection';
import { PenTool, Users, MessageSquare, Layout, Palette, BookOpen } from 'lucide-react';
import ScrollReveal from '@/components/ui/ScrollReveal';
import ServiceCard from '@/components/ui/ServiceCard';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const brandingServices = [
  {
    icon: <PenTool />,
    title: "Logo Design",
    description: "Professional logo design that captures your brand essence and creates a memorable visual identity for your business.",
    badge: "Visual Identity"
  },
  {
    icon: <Palette />,
    title: "Brand Identity",
    description: "Complete brand identity systems including color palettes, typography, and visual elements that create a cohesive brand experience.",
    badge: "Identity"
  },
  {
    icon: <MessageSquare />,
    title: "Brand Messaging",
    description: "Clear and compelling brand messaging that resonates with your target audience and communicates your unique value proposition.",
    badge: "Messaging"
  },
  {
    icon: <Users />,
    title: "Audience Research",
    description: "In-depth research to understand your target audience's needs, preferences, and pain points to inform your branding strategy.",
    badge: "Research"
  },
  {
    icon: <BookOpen />,
    title: "Brand Guidelines",
    description: "Comprehensive brand guidelines that ensure consistency across all marketing materials and touchpoints.",
    badge: "Guidelines"
  },
  {
    icon: <Layout />,
    title: "Brand Applications",
    description: "Application of your brand across various marketing materials including business cards, stationery, social media, and more.",
    badge: "Applications"
  }
];

const faqs = [
  {
    question: "How long does a branding project typically take?",
    answer: "The timeline for a branding project varies depending on its scope. A basic logo design might take 2-3 weeks, while a comprehensive brand identity development can take 6-8 weeks. During our initial consultation, we'll discuss your specific needs and provide a more accurate timeline for your project."
  },
  {
    question: "Do I need a complete brand identity, or just a logo?",
    answer: "While a logo is an important part of your brand, a complete brand identity provides a more comprehensive foundation for your business. For new businesses or those undergoing significant changes, we recommend a full brand identity. However, we can tailor our services to your specific needs and budget, whether that's just a logo or a complete branding package."
  },
  {
    question: "How do you ensure the brand you create will resonate with my target audience?",
    answer: "Our branding process begins with thorough research into your industry, competitors, and target audience. We conduct discovery sessions to understand your business goals and values, and we may also conduct customer interviews or surveys. This research-driven approach ensures that the brand we create is not only visually appealing but strategically aligned with your audience's expectations and preferences."
  },
  {
    question: "Will I own all the rights to my brand assets?",
    answer: "Yes, once the project is complete and final payment is made, you will own all rights to the brand assets we create for you. This includes your logo, color palette, typography, and any other visual elements developed during the branding process. We provide all necessary file formats for both digital and print applications."
  },
  {
    question: "How do you handle revisions during the branding process?",
    answer: "Our branding packages typically include two to three rounds of revisions at each stage of the process. This allows us to refine the concepts based on your feedback while maintaining project momentum. If additional revisions are needed beyond what's included in your package, we can accommodate them at an hourly rate. We're committed to ensuring you're completely satisfied with the final result."
  }
];

const Branding = () => {
  return (
    <PageLayout 
      title="Branding & Identity | Astrem" 
      description="Brand development including logo design, messaging, and creating a cohesive identity that resonates with your audience."
      ogImage="public/lovable-uploads/c437cfb3-440c-4c1f-85ba-2c1cdee5243a.png"
    >
      <PageHero 
        title="Branding & Identity Services" 
        subtitle="Create a memorable brand that resonates with your audience" 
        description="Our branding services help businesses develop a distinctive and cohesive brand identity that captures their essence, communicates their value, and creates meaningful connections with their target audience."
        badge="Brand Development"
        backgroundImage="https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3"
        ctaLink="/contact"
      />
      
      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Branding Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              We provide comprehensive branding services to help your business establish a distinctive and memorable brand identity.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {brandingServices.map((service, index) => (
              <ScrollReveal key={index} delay={200 + (index * 50)}>
                <ServiceCard
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  badge={service.badge}
                  href="https://tidycal.com/evangutierrez/free-brand-consultation"
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* Logo Showcase Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Brand Logo Portfolio
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              We've created distinctive and impactful logos for a variety of businesses across different industries.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={200}>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <img 
                  src="https://app.milanote.com/media/p/images/1U0GkG1kNhsq1X/8B6/TimelessHoopsLogo-Orange.jpg" 
                  alt="Timeless Hoops Logo" 
                  className="w-full h-48 object-contain mb-4" 
                />
                <h3 className="text-xl font-medium mb-2">Timeless Hoops</h3>
                <p className="text-astrem-gray-600">Basketball Video Course Program</p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={250}>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <img 
                  src="https://app.milanote.com/media/p/images/1U0Glb1kNhsq1Y/dpI/PinnacleRoofingLogo-Red.jpg" 
                  alt="Pinnacle Roofing Logo" 
                  className="w-full h-48 object-contain mb-4" 
                />
                <h3 className="text-xl font-medium mb-2">Pinnacle Roofing</h3>
                <p className="text-astrem-gray-600">Baton Rouge Roofing Contractor</p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <img 
                  src="https://app.milanote.com/media/p/images/1U0Glw1kNhsq1Z/F6H/Caard%20Front.jpg" 
                  alt="Tablas Wood Creations" 
                  className="w-full h-48 object-contain mb-4" 
                />
                <h3 className="text-xl font-medium mb-2">Tablas Wood Creations</h3>
                <p className="text-astrem-gray-600">Gonzales, Louisiana Custom Furniture Builder</p>
              </div>
            </ScrollReveal>
          </div>
          
          <div className="text-center mt-12">
            <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
              <Link to="/contact">Get Your Custom Logo Design</Link>
            </Button>
          </div>
        </div>
      </section>
      
      {/* Logo Design Section */}
      <section id="logo-design" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200}>
              <img 
                src="https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3" 
                alt="Logo design process" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
            
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Visual Identity
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Logo & Brand Book
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Complete branding package that includes Advanced Branding Research and Analysis plus a full 20-page Corporate Identity guide to establish your visual brand.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Logo transparency</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Vector/source file</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Printable file</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Fast delivery</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Color palettes</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Brand typography</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">3D mockups</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Stationery designs</span>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Social media kit</span>
                  </div>
                </div>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Contact for Quote</Link>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Brand Consultation Section */}
      <section id="brand-consultation" className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Consultation
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Brand Consultation Services
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Get expert guidance on your brand strategy, market positioning, and visual identity from our experienced branding consultants.
                </p>
                
                <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                  <h3 className="text-xl font-medium mb-2">Free 15-Minute Intro Call</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    We'll discuss your brand, project goals, current state of marketing and efforts, and plan for next steps.
                  </p>
                  <Button asChild variant="outline" className="w-full rounded-full">
                    <Link to="/contact">Contact for Quote</Link>
                  </Button>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                  <h3 className="text-xl font-medium mb-2">Full Consultation Package</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    4 hours of in-depth consultation covering mind mapping, strategy development, insights, education, brainstorming, technology integration, and diverse perspectives.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">Mind map ideas</span>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">Strategy ideas</span>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">Insight</span>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">Education</span>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">Brainstorming</span>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">Technology</span>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">Perspectives</span>
                    </div>
                    <div className="flex items-start">
                      <div className="mr-2 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm text-astrem-gray-700">App Development</span>
                    </div>
                  </div>
                  <Button asChild className="w-full rounded-full">
                    <Link to="/contact">Contact for Quote</Link>
                  </Button>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-medium mb-2">Additional Services</h3>
                  <p className="text-astrem-gray-600 mb-4">
                    Contact us for custom quotes on these additional branding services:
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <div className="mr-2 text-primary">•</div>
                      <span className="text-astrem-gray-700">Stationery Design</span>
                    </li>
                    <li className="flex items-center">
                      <div className="mr-2 text-primary">•</div>
                      <span className="text-astrem-gray-700">Business Card Designs</span>
                    </li>
                    <li className="flex items-center">
                      <div className="mr-2 text-primary">•</div>
                      <span className="text-astrem-gray-700">Website Design</span>
                    </li>
                    <li className="flex items-center">
                      <div className="mr-2 text-primary">•</div>
                      <span className="text-astrem-gray-700">Social Media Setup and Configuration</span>
                    </li>
                  </ul>
                  <Button asChild variant="outline" className="w-full rounded-full">
                    <Link to="/contact">Contact for Quote</Link>
                  </Button>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img 
                src="https://images.unsplash.com/photo-1616628188859-7a11abb6fcc9?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3" 
                alt="Brand consultation session" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Branding Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Branding Process
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              We follow a strategic process to create brands that are distinctive, memorable, and aligned with your business goals.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <ScrollReveal delay={200}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">1</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Discovery</h3>
                <p className="text-astrem-gray-600">
                  We research your industry, competition, and target audience to understand your unique position in the market.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">2</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Strategy</h3>
                <p className="text-astrem-gray-600">
                  We develop a brand strategy that defines your positioning, messaging, and visual direction based on our research.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">3</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Creation</h3>
                <p className="text-astrem-gray-600">
                  We create visual and verbal brand elements that bring your brand to life in a cohesive and compelling way.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={500}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">4</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Implementation</h3>
                <p className="text-astrem-gray-600">
                  We provide guidelines and assets that help you consistently apply your brand across all touchpoints.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
              Ready to Build Your Brand?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Let's discuss how our branding services can help you create a distinctive and memorable brand that resonates with your audience.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
              <Link to="/contact">Contact for Quote</Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>
      
      <FAQSection faqs={faqs} />
    </PageLayout>
  );
};

export default Branding;
